import { useCallback, useEffect, useMemo } from 'react';

import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { groupPresences } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import { GroupedPresencesType } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/hooks/useBatchSelection';
import {
  FrameActionType,
  GroupingPresencesCriteria,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { flattenCollection } from '@/features/broadcast-schedule-program/helpers/flatten-collection';
import { sortGroupPresences } from '@/features/broadcast-schedule-program/helpers/sort-by-key';
import { useLogosById, usePeopleById } from '@/hooks/use-catalogue-entries-by-id';
import { SortOrder } from '@/types';
import { TPresenceItem } from '@/types/presences';

export function useGroupedPresences<T extends TPresenceItem>(
  items: T[],
  onPreview?: (item: T, frame?: FrameActionType) => void,
  entityIdKey?: keyof T
) {
  const resolvedEntityIdKey: keyof T = entityIdKey ?? ('logo_id' as keyof T);
  const { byId: logosById } = useLogosById(
    items.map(item => (item as { logo_id?: string }).logo_id),
    { keepPrevious: true }
  );
  const { byId: peopleById } = usePeopleById(
    items.map(item => (item as { people_id?: string }).people_id),
    { keepPrevious: true }
  );
  const { presenceData } = useBroadcastScheduleProgram();
  const { toolkitCardRegionRef } = useBroadcastScheduleProgram();

  const {
    groupPresenceCriteria,
    setGroupedPresences,
    groupedPresences,
    setGroupPresenceCriteria,
    sortDirection,
    setSortDirection,
    isAutoplayingClips,
    setAutoplayingClips,
    expandAllPresences,
    setExpandAllPresences,
    clearRowExpandOverrides,
  } = useBroadcastScheduleContext();

  useEffect(() => {
    clearRowExpandOverrides();
  }, [expandAllPresences, clearRowExpandOverrides]);

  useEffect(() => {
    const sorted = sortGroupPresences(
      groupPresences<T>(items, groupPresenceCriteria, resolvedEntityIdKey) as GroupedPresencesType,
      undefined,
      sortDirection
    );
    setGroupedPresences(sorted);
  }, [items]);

  const totalPresences = useMemo(
    () => groupedPresences.reduce((n, item) => n + ((item as any).children?.length ?? 0), 0),
    [groupedPresences]
  );

  const areGroupsOnGroupedLogos = useMemo(
    () =>
      groupedPresences.some(item => (item as any).children && (item as any).children.length > 0),
    [groupedPresences]
  );

  const indexLogos = useMemo(() => Object.fromEntries(logosById), [logosById]);

  const indexPeople = useMemo(() => Object.fromEntries(peopleById), [peopleById]);

  const onChangeCriteria = (criteria: string) => {
    const grouped = sortGroupPresences(
      groupPresences(
        items,
        criteria as GroupingPresencesCriteria,
        resolvedEntityIdKey
      ) as GroupedPresencesType,
      undefined,
      sortDirection
    );
    setGroupPresenceCriteria(criteria as GroupingPresencesCriteria);
    setGroupedPresences(prev => {
      const isEqual = JSON.stringify(prev) === JSON.stringify(grouped);
      return isEqual ? prev : grouped;
    });
  };

  const onChangeSortDirection = (direction: string) => {
    const newDirection = direction as SortOrder;
    const sorted = sortGroupPresences(
      groupPresences(items, groupPresenceCriteria, resolvedEntityIdKey) as GroupedPresencesType,
      undefined,
      newDirection
    );
    setSortDirection(newDirection);
    setGroupedPresences(prev => {
      const isEqual = JSON.stringify(prev) === JSON.stringify(sorted);
      return isEqual ? prev : sorted;
    });
  };

  const scrollToEndTable = () => {
    const el = toolkitCardRegionRef.current;
    if (el) {
      setTimeout(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }, 300);
    }
  };

  const onChangeAutoplayClipsMode = () => {
    if (!presenceData) {
      const flattenCollection2 = flattenCollection<T>(groupedPresences as T[]);
      const firstDetected = flattenCollection2[flattenCollection2.length - 1];
      onPreview && onPreview(firstDetected);
      setExpandAllPresences(true);
      scrollToEndTable();
    }

    setAutoplayingClips(!isAutoplayingClips);
  };

  const getRowClassStyles = useCallback(() => ({ row: 'bg-gray-100' }), []);

  return {
    totalPresences,
    areGroupsOnGroupedLogos,
    indexLogos,
    indexPeople,
    logosById,
    peopleById,
    onChangeCriteria,
    onChangeSortDirection,
    sortDirection,
    onChangeAutoplayClipsMode,
    getRowClassStyles,
  };
}
